import { nanoid } from 'nanoid';
import { API_URL } from '../configs/commons';
import { Blog } from '@/types/blog.types';

export const generateId = (length: number = 10) => {
    return nanoid(length);
}

export const coerceWidth = (width: string): string => {
    return width ? width.replace(/\D/g, '') : width;
}

export const coerceNumber = (width: string): string => {
    return width ? width.replace(/\D/g, '') : width;
}

export const strapiImage = (url: string): string => {
    return /^https?:\/\//.test(url) ? url : `${API_URL}${url}`;
}

export const transformBlogList = (data: Blog[]): any[] => {
    return data?.map((blog: Blog) => ({
        imageUrl: blog.coverImage && strapiImage(blog.coverImage.image.url),
        highlightValue: blog.bigHighlight,
        highlightLabel: blog.smallHighlight,
        author: (blog.authors && blog.authors.length > 0 && `${blog.authors[0].firstName} ${blog.authors[0].lastName}`),
        readTime: blog.timeToRead,
        title: blog.title,
        description: blog.description,
        tags: blog.categories.map(item => item.categoryName),
        buttonLabel: 'Read Story',
        buttonLink: `/blog/${blog.slug}`,
        isVideo: !!blog.blogVideo,
        isPopular: blog.isPopular,
        bgColor: blog.bgColor,
        id: blog.id,
    }))
}

export const validateEmail = (email: string): string | null => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email) {
        return "Email is required.";
    } else if (email.length > 255) {
        return "You have entered more than 255 characters.";
    } else if (!regex.test(email)) {
        return "Invalid email format.";
    }

    return null;
};

export const extractYouTubeLink = (html: string): string | null => {
    if (typeof window === 'undefined') return null;

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const links = Array.from(doc.querySelectorAll('a'));

    for (const link of links) {
        const href = link.getAttribute('href')?.trim() || '';
        const text = link.textContent?.trim() || '';

        if (href === text) {
            const match = href.match(
                /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/
            );
            if (match && match[1]) {
                return `https://www.youtube.com/embed/${match[1]}`;
            }

            return href;
        }

        return null;
    }

    return null;
};

export const handleMenuClick = (event: React.MouseEvent<HTMLAnchorElement>, link: string) => {
    event.preventDefault();
    const isLiveChat = link.includes('24-live-chat');
    const isIntercomReady = typeof window !== 'undefined' && typeof window.Intercom === 'function';

    if (isLiveChat && isIntercomReady) {
        window.Intercom!('show');
    } else {
        window.location.href = link;
    }
};

export const hasNumber = (value: string | number): boolean => {
    if (typeof value === 'number') {
        value = value.toString();
    }
    return /\d/.test(value);
}

export const generatePadding = (
    padding?: { desktop?: string | number; mobile?: string | number; tablet?: string | number },
    isMobile?: boolean,
    isTablet?: boolean
): string | undefined => {
    if (!padding) return undefined;

    if (isMobile) {
        return padding.mobile !== undefined ? `${padding.mobile}rem` : undefined;
    } else if (isTablet) {
        return padding.tablet !== undefined ? `${padding.tablet}rem` : undefined;
    } else {
        return padding.desktop !== undefined ? `${padding.desktop}rem` : undefined;
    }
};

export const calculateMonthlyPrice = (amount: number, billingCycle: 'Monthly' | 'Quarterly' | 'Annual'): number => {
    let divisor = 1;
    if (billingCycle === 'Quarterly') {
        divisor = 3;
    } else if (billingCycle === 'Annual') {
        divisor = 12;
    }
    // Divide the amount by the appropriate divisor and round up to the nearest integer
    return Math.ceil(amount / divisor);
};